// AuthStory.jsx
//
// La animación de la pantalla de acceso (#1439, mockup aprobado
// tools/mockups/smartdx_mockups_navbar_acceso_e_v2.html, "Acceso 2"):
// tres capítulos que se repiten en bucle, cada uno con la UI del Modo Turno
// dibujada en HTML (no capturas: se ven nítidas a cualquier tamaño).
//
//   01 SmartDx sincroniza camas desde el computador
//   02 Toma nota desde el celular
//   03 Cierra tus evoluciones cuando quieras
//
// Regla de Sebastián (02-oct-2026): el registro generado se muestra COMPLETO y
// hasta el final mientras se escribe. Por eso cada capítulo espera a que su
// texto termine antes de pasar al siguiente: la barra de progreso es solo una
// estimación y nunca corta la escritura.
//
// Todo es de ejemplo: el paciente es el demo de la app ("Hernán Sepúlveda
// Rojas (DEMO)") y la nota y el resumen son los que la app muestra con él.
// Va dentro del alcance oscuro de AuthShell, así que cada variable CSS resuelve
// al valor del modo oscuro aunque el médico use el modo claro.
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";

import { serif } from "./auth-styles";

const CAMAS = [
  ["A", "Hernán Sepúlveda R.", "IC FEVI reducida · NAC"],
  ["B", "Rosa Medina V.", "NAC multilobar"],
  ["C", "Jorge Pizarro L.", "ITU alta · DM2"],
  ["D", "Elena Vidal C.", "Pancreatitis aguda"],
  ["E", "Luis Cárcamo P.", "EPOC exacerbado"],
  ["F", "Marta Araya S.", "ACV isquémico"],
];
const CENSO = [
  "301A  SEPULVEDA H.  78  ICC",
  "301B  MEDINA R.     64  NAC",
  "302A  PIZARRO J.    71  ITU",
  "302B  VIDAL E.      45  PANCREAT",
  "303A  CARCAMO L.    69  EPOC",
  "303B  ARAYA M.      82  ACV",
].join("\n");
const NOTA =
  "dia 5 hosp. amanece mejor, sin ortopnea, durmio plano. afebril 72h. sat 94% con 1L. BH -1.100 ayer, diuresis 2.300. peso 71,4 (ingreso 75,2). edema EEII +.\n" +
  "PA 124/70 FC 84 irregular FR 18 T 36,6 HGT 162\n" +
  "lab hoy: crea 1,9 K 4,6 Na 134 PCR 48 Hb 10,9 INR 2,4\n" +
  "plan: pasar furo a VO 40 c/12 mañana si sigue negativo, cumple 5d ceftriaxona -> amoxi-clav VO x 2 dias, reiniciar espironolactona 12,5 si K <5, pedir interconsulta cardio x ajuste IECA/ARNI con crea y x TACO. kine motora. alta probable lunes si tolera.";
// Las líneas con "§" son títulos.
const RESUMEN =
  "§Diagnósticos actuales\n" +
  "1. IC FEVI reducida descompensada (FEVI 28%, sobrecarga hídrica, edema EEII, en ajuste de diuréticos)\n" +
  "   - NAC basal izquierda\n" +
  "      - FI 22/09 Ceftriaxona + Azitromicina\n" +
  "      - 26/09 inicia amoxi-clav VO\n" +
  "   - AKI sobre ERC 3b (Crea peak 2,6, hoy 1,9)\n" +
  "      - Furosemida EV 40 mg c/8 (en transición a VO)\n" +
  "   - FA permanente en TACO\n" +
  "2. Comorbilidades: HTA, DM2 insulinorrequiriente\n\n" +
  "§Evolución 26/09/2026\n" +
  "Día 5 de hospitalización (ingreso 22/09). Ingreso por descompensación de IC, NAC izquierda y AKI. Al día de hoy, mejoría clínica: afebril 72h, sin ortopnea, tolerando decúbito, balance negativo acumulado, saturando 94% con 1L, edema EEII +. Estable, sin DVA, evolución favorable hacia alta.\n\n" +
  "BH: -1.100 mL ayer, negativo acumulado desde ingreso\n" +
  "Diuresis: 2.300 mL/24h\n" +
  "Deposiciones: Sin datos";

export const CAPITULOS = [
  { n: "01", titulo: "SmartDx sincroniza camas", em: "desde el computador", estimadoMs: 5200 },
  { n: "02", titulo: "Toma nota", em: "desde el celular", estimadoMs: 8600 },
  { n: "03", titulo: "Cierra tus evoluciones", em: "cuando quieras", estimadoMs: 9000 },
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const reduceMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ---------- piezas de UI del Turno (siempre oscuras) ----------
const linea = "1px solid rgba(var(--fg-rgb),0.09)";
const chipSx = (rgb, color) => ({
  font: "700 9.5px/1 Inter, sans-serif", letterSpacing: "0.12em", px: "7px", py: "4px",
  borderRadius: "6px", color, backgroundColor: `rgba(var(${rgb}),0.14)`,
});
const ventanaSx = {
  position: "absolute", backgroundColor: "var(--bg-base)", border: "1px solid rgba(var(--fg-rgb),0.14)",
  borderRadius: "14px", overflow: "hidden", boxShadow: "0 50px 100px -30px rgba(var(--shadow-rgb),0.85)",
  color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.5,
};
const BarraUrl = () => (
  <Box sx={{ height: 32, display: "flex", alignItems: "center", gap: "7px", px: "12px", borderBottom: linea, backgroundColor: "var(--bg-deep)" }}>
    {[0, 1, 2].map((i) => <Box key={i} sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "rgba(var(--fg-rgb),0.16)" }} />)}
    <Box sx={{ ml: "10px", fontSize: 11, color: "rgba(var(--fg-rgb),0.45)", backgroundColor: "rgba(var(--fg-rgb),0.05)", borderRadius: "6px", px: "10px", py: "2px" }}>
      app.smardx.com/turno
    </Box>
  </Box>
);
const Unidad = () => (
  <Box component="span" sx={{ fontWeight: 600 }}>
    30/09 · <Box component="span" sx={{ color: "var(--color-primary-soft)" }}>Medicina 3</Box>
  </Box>
);
const conTitulos = (txt) =>
  txt.split("\n").map((l, i, arr) => (
    <React.Fragment key={i}>
      {l.startsWith("§") ? <Box component="b" sx={{ color: "var(--text-primary)", fontWeight: 600 }}>{l.slice(1)}</Box> : l}
      {i < arr.length - 1 ? "\n" : null}
    </React.Fragment>
  ));
const Cursor = () => (
  <Box component="span" sx={{
    display: "inline-block", width: "1.5px", height: 13, ml: "1px", verticalAlign: "-2px",
    backgroundColor: "var(--color-info)", animation: "authCursor 1s steps(1) infinite",
    "@keyframes authCursor": { "50%": { opacity: 0 } },
  }} />
);

// Texto que se escribe solo y sigue el final: el contenedor baja mientras crece.
function useEscritura(texto, activo, { paso = 3, cadaMs = 26, onFin }) {
  const [n, setN] = useState(0);
  const fin = useRef(onFin);
  fin.current = onFin;
  useEffect(() => {
    if (!activo) { setN(0); return undefined; }
    if (reduceMotion()) { setN(texto.length); fin.current && fin.current(); return undefined; }
    let k = 0;
    const iv = setInterval(() => {
      k = Math.min(texto.length, k + paso + (Math.random() < 0.2 ? 2 : 0));
      setN(k);
      if (k >= texto.length) { clearInterval(iv); fin.current && fin.current(); }
    }, cadaMs);
    return () => clearInterval(iv);
  }, [texto, activo, paso, cadaMs]);
  return n;
}
function useSigueElFinal(ref, dep) {
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [ref, dep]);
}

// ---------- capítulo 1: camas ----------
function CapCamas({ activo, onFin }) {
  const [vistas, setVistas] = useState(0);
  const [censoFuera, setCensoFuera] = useState(false);
  const [aviso, setAviso] = useState(false);
  const fin = useRef(onFin);
  fin.current = onFin;
  useEffect(() => {
    if (!activo) { setVistas(0); setCensoFuera(false); setAviso(false); return undefined; }
    if (reduceMotion()) { setVistas(CAMAS.length); setCensoFuera(true); setAviso(true); fin.current(); return undefined; }
    let vivo = true;
    (async () => {
      await sleep(700); if (!vivo) return; setCensoFuera(true);
      for (let i = 1; i <= CAMAS.length; i += 1) { await sleep(280); if (!vivo) return; setVistas(i); }
      await sleep(300); if (!vivo) return; setAviso(true);
      await sleep(3200); if (vivo) fin.current();
    })();
    return () => { vivo = false; };
  }, [activo]);
  return (
    <Box sx={{ ...ventanaSx, left: 20, top: 90, width: 580, height: 410 }}>
      <BarraUrl />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: "14px", py: "10px", borderBottom: linea }}>
        <span><Box component="b" sx={{ fontSize: 16, color: "var(--text-primary)" }}>Turno</Box>{" "}
          <Box component="span" sx={{ color: "var(--text-muted)" }}>30/09 · {vistas} pacientes</Box></span>
        <Unidad />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "260px 1fr", height: "calc(100% - 77px)" }}>
        <Box sx={{ borderRight: linea }}>
          {CAMAS.map((c, i) => (
            <Box key={c[0]} sx={{
              display: "grid", gridTemplateColumns: "18px 1fr 8px", gap: "10px", alignItems: "center",
              px: "12px", py: "9px", borderBottom: linea,
              opacity: i < vistas ? 1 : 0, transform: i < vistas ? "none" : "translateX(-10px)",
              transition: "opacity 360ms cubic-bezier(0.22,1,0.36,1), transform 360ms cubic-bezier(0.22,1,0.36,1)",
            }}>
              <Box component="b" sx={{ fontSize: 12 }}>{c[0]}</Box>
              <Box><Box component="b" sx={{ fontSize: 12.5, color: "var(--text-primary)" }}>{c[1]}</Box>
                <Box sx={{ fontSize: 11, color: "var(--text-muted)" }}>{c[2]}</Box></Box>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "var(--color-success)" }} />
            </Box>
          ))}
        </Box>
        <Box sx={{ position: "relative", p: "14px" }}>
          <Box sx={{
            position: "absolute", left: 18, top: 12, px: "13px", py: "11px", borderRadius: "12px",
            font: "10.5px/1.7 ui-monospace, Menlo, monospace", whiteSpace: "pre", color: "rgba(var(--fg-rgb),0.7)",
            backgroundColor: "rgba(var(--fg-rgb),0.07)", border: "1px dashed rgba(var(--fg-rgb),0.25)",
            transition: "transform 900ms cubic-bezier(0.22,1,0.36,1), opacity 600ms",
            transform: censoFuera ? "translate(-200px, 90px) scale(0.5)" : "none", opacity: censoFuera ? 0 : 1,
          }}>{CENSO}</Box>
          <Box sx={{
            position: "absolute", left: "50%", bottom: 14, whiteSpace: "nowrap", fontSize: 12.5,
            px: "14px", py: "9px", borderRadius: "12px", backgroundColor: "var(--bg-elevated)",
            border: "1px solid rgba(var(--color-success-rgb),0.4)",
            transform: aviso ? "translate(-50%, 0)" : "translate(-50%, 14px)", opacity: aviso ? 1 : 0,
            transition: "all 400ms cubic-bezier(0.22,1,0.36,1)",
          }}>
            <Box component="b" sx={{ color: "var(--color-success)" }}>✓</Box>{" "}
            <Box component="b" sx={{ color: "var(--text-primary)" }}>6 camas sincronizadas</Box>{" "}
            <Box component="span" sx={{ color: "var(--text-muted)" }}>· 1 nueva · 1 alta</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---------- capítulo 2: nota en el celular ----------
function CapNota({ activo, onFin }) {
  const caja = useRef(null);
  const [listo, setListo] = useState(false);
  const fin = useRef(onFin);
  fin.current = onFin;
  const n = useEscritura(NOTA, activo, { paso: 3, cadaMs: 26, onFin: () => setListo(true) });
  useSigueElFinal(caja, n);
  useEffect(() => {
    if (!activo) { setListo(false); return undefined; }
    if (!listo) return undefined;
    const t = setTimeout(() => fin.current(), reduceMotion() ? 0 : 3000);
    return () => clearTimeout(t);
  }, [activo, listo]);
  return (
    <Box sx={{
      position: "absolute", left: 165, top: 20, width: 290, height: 580, borderRadius: "44px", p: "10px",
      backgroundColor: "var(--bg-deep)", border: "1px solid rgba(var(--fg-rgb),0.2)",
      boxShadow: "0 50px 100px -30px rgba(var(--shadow-rgb),0.9)",
    }}>
      <Box sx={{ height: "100%", borderRadius: "35px", backgroundColor: "var(--bg-base)", overflow: "hidden", px: "14px", pt: "14px", color: "var(--text-secondary)", fontSize: 13 }}>
        <Box sx={{ width: 90, height: 26, borderRadius: "20px", backgroundColor: "rgba(var(--shadow-rgb),1)", mx: "auto", mb: "10px" }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid rgba(var(--fg-rgb),0.14)", borderRadius: "999px", px: "14px", py: "7px", mx: "28px", mb: "10px", fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }}>
          <span>Smart<Box component="span" sx={{ color: "var(--color-primary-soft)" }}>Dx</Box></span><span style={{ opacity: 0.7 }}>☰</span>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}><Unidad /><Box component="span" sx={{ color: "var(--text-muted)" }}>··· Acciones</Box></Box>
        <Box sx={{ mt: "10px", fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }}>Hernán Sepúlveda R.</Box>
        <Box sx={{ fontSize: 11.5, color: "var(--text-muted)" }}>78a · IC FEVI reducida · NAC · AKI</Box>
        <Box sx={{ mt: "12px", height: 300, backgroundColor: "var(--bg-elevated)", border: linea, borderRadius: "13px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: "12px", py: "9px", borderBottom: linea, fontWeight: 600, fontSize: 12.5, color: "var(--text-primary)" }}>
            <Box component="span" sx={chipSx("--color-warning-rgb", "var(--color-warning)")}>DIARIA</Box>Actualización diaria
          </Box>
          <Box ref={caja} sx={{ px: "12px", py: "10px", fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap", overflow: "hidden", flex: 1, minHeight: 0 }}>
            {NOTA.slice(0, n)}{!listo && <Cursor />}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "10px", alignItems: "center", mt: "10px", px: "10px", py: "8px", borderRadius: "16px", backgroundColor: "var(--bg-elevated)", border: "1px solid rgba(var(--fg-rgb),0.12)" }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "var(--color-danger)", flex: "none", display: "grid", placeItems: "center", color: "var(--text-primary)" }}>
            <MicNoneOutlinedIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box sx={{ display: "flex", gap: "2px", alignItems: "center", height: 22, flex: 1 }}>
            {Array.from({ length: 28 }, (_, i) => (
              <Box key={i} sx={{
                width: "2.5px", height: "100%", borderRadius: "2px", backgroundColor: "var(--color-success)",
                animation: listo ? "none" : `authOnda .9s ease-in-out ${((i * 0.07) % 0.9).toFixed(2)}s infinite`,
                transform: listo ? "scaleY(.2)" : undefined,
                "@keyframes authOnda": { "0%, 100%": { transform: "scaleY(.2)" }, "50%": { transform: "scaleY(1)" } },
              }} />
            ))}
          </Box>
          <Box component="span" sx={{ font: "600 11px ui-monospace, Menlo, monospace", color: "var(--text-muted)" }}>0:42</Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---------- capítulo 3: la evolución, escrita entera ----------
function CapEvolucion({ activo, onFin }) {
  const caja = useRef(null);
  const [listo, setListo] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const fin = useRef(onFin);
  fin.current = onFin;
  const n = useEscritura(RESUMEN, activo, { paso: 4, cadaMs: 16, onFin: () => setListo(true) });
  useSigueElFinal(caja, n);
  useEffect(() => {
    if (!activo) { setListo(false); setCopiado(false); return undefined; }
    if (!listo) return undefined;
    let vivo = true;
    (async () => {
      await sleep(reduceMotion() ? 0 : 900); if (!vivo) return; setCopiado(true);
      await sleep(reduceMotion() ? 0 : 3500); if (vivo) fin.current();
    })();
    return () => { vivo = false; };
  }, [activo, listo]);
  return (
    <Box sx={{
      ...ventanaSx, left: 30, top: 50, width: 560, height: 520, backgroundColor: "var(--bg-elevated)",
      borderColor: "rgba(var(--color-success-rgb),0.35)", display: "flex", flexDirection: "column",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: "12px", py: "9px", borderBottom: linea, fontWeight: 600, fontSize: 12.5, color: "var(--text-primary)" }}>
        <Box component="span" sx={chipSx("--color-success-rgb", "var(--color-success)")}>EVOLUCIÓN</Box>
        <Box component="span" sx={{ px: "9px", py: "3px", borderRadius: "8px", backgroundColor: "rgba(var(--fg-rgb),0.06)" }}>Resumen clínico ⌄</Box>
        <Box component="span" sx={{
          ml: "auto", px: "11px", py: "4px", borderRadius: "999px", fontSize: 11.5, fontWeight: 600, transition: "all 200ms",
          color: copiado ? "var(--color-success)" : "var(--bg-base)",
          backgroundColor: copiado ? "rgba(var(--color-success-rgb),0.18)" : "var(--text-primary)",
        }}>{copiado ? "Copiado ✓" : "Copiar"}</Box>
      </Box>
      <Box ref={caja} sx={{ px: "12px", py: "10px", fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap", overflow: "hidden", flex: 1, minHeight: 0 }}>
        {conTitulos(RESUMEN.slice(0, n))}{!listo && <Cursor />}
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", px: "12px", py: "10px", borderTop: linea }}>
        {["En la visita", "Al final del turno", "Desde la casa"].map((t, i) => (
          <Box key={t} component="span" sx={{
            fontSize: 11.5, px: "10px", py: "4px", borderRadius: "999px",
            backgroundColor: i === 1 ? "rgba(var(--fg-rgb),0.1)" : "rgba(var(--fg-rgb),0.06)",
            color: i === 1 ? "var(--color-secondary)" : "var(--text-tertiary)",
          }}>{t}</Box>
        ))}
      </Box>
    </Box>
  );
}

const CAPS = [CapCamas, CapNota, CapEvolucion];
const ESCENARIO = 620; // las escenas se dibujan en 620x620 y se escalan al hueco

/**
 * `compacto` = celular: la escena va más chica y el título del capítulo, en una
 * línea más corta. El escalado lo calcula un ResizeObserver sobre el hueco.
 */
export default function AuthStory({ compacto = false }) {
  const [cap, setCap] = useState(0);
  const [vuelta, setVuelta] = useState(0); // fuerza reiniciar el capítulo al volver a él
  const hueco = useRef(null);
  const [escala, setEscala] = useState(1);

  useEffect(() => {
    const el = hueco.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      const { width, height } = el.getBoundingClientRect();
      setEscala(Math.max(0.3, Math.min(width / ESCENARIO, height / ESCENARIO, 1.15)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const siguiente = () => {
    setCap((c) => (c + 1) % CAPS.length);
    setVuelta((v) => v + 1);
  };

  return (
    <Box sx={{ position: "absolute", inset: 0 }} aria-hidden="true">
      <Box ref={hueco} sx={{ position: "absolute", left: 0, right: 0, top: 0, bottom: compacto ? 84 : 190 }}>
        {CAPS.map((Cap, i) => (
          <Box key={i} sx={{
            position: "absolute", left: "50%", top: "50%", width: ESCENARIO, height: ESCENARIO,
            transform: `translate(-50%, -50%) scale(${escala})`,
            opacity: i === cap ? 1 : 0, transition: "opacity 600ms cubic-bezier(0.22,1,0.36,1)",
            pointerEvents: "none",
          }}>
            <Cap key={i === cap ? `on-${vuelta}` : "off"} activo={i === cap} onFin={siguiente} />
          </Box>
        ))}
      </Box>
      {/* El título y las barras se ajustan al ancho del hueco (que en escritorio
          termina donde empieza el vidrio): con un ancho fijo quedaban tapados
          entre 900 y ~1250 px (revisor #1439). */}
      <Box sx={{ position: "absolute", left: compacto ? 20 : 64, right: compacto ? 20 : 48, bottom: compacto ? 20 : 64, maxWidth: compacto ? "none" : 560 }}>
        <Box sx={{ position: "relative", minHeight: compacto ? 58 : 100 }}>
          {CAPITULOS.map((c, i) => (
            <Box key={c.n} sx={{ position: "absolute", left: 0, bottom: 0, opacity: i === cap ? 1 : 0, transition: "opacity 600ms cubic-bezier(0.22,1,0.36,1)" }}>
              <Typography sx={{ font: "600 12px ui-monospace, Menlo, monospace", letterSpacing: "0.08em", color: "rgba(var(--fg-rgb),0.55)" }}>{c.n} / 03</Typography>
              <Typography component="p" sx={{ m: 0, mt: compacto ? 0.25 : 1, fontSize: compacto ? 22 : { md: 32, lg: 40 }, lineHeight: 1.05, letterSpacing: "-0.035em", fontWeight: 500, color: "var(--text-primary)" }}>
                {c.titulo}{" "}
                <Box component="span" sx={{ fontFamily: serif, fontStyle: "italic", fontWeight: 400, color: "var(--color-secondary)" }}>{c.em}</Box>
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, mt: compacto ? 1.25 : 3 }}>
          {CAPITULOS.map((c, i) => (
            <Box key={c.n} sx={{ position: "relative", height: 3, borderRadius: 2, flex: 1, overflow: "hidden", backgroundColor: "rgba(var(--fg-rgb),0.22)" }}>
              <Box
                key={i === cap ? `b-${vuelta}` : "b"}
                sx={{
                  position: "absolute", inset: "0 auto 0 0", backgroundColor: "var(--text-primary)",
                  width: i < cap ? "100%" : "0%",
                  animation: i === cap && !reduceMotion() ? `authBarra ${c.estimadoMs}ms linear forwards` : "none",
                  "@keyframes authBarra": { from: { width: "0%" }, to: { width: "100%" } },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
